import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

export const ButtonVariants = ['primary', 'secondary', 'tertiary', 'outline'] as const;
type ButtonVariant = (typeof ButtonVariants)[number];

type ButtonProps = {
	children: React.ReactNode;
	onClick?: () => void;
	variant?: ButtonVariant;
	isLink?: boolean;
	href?: string;
	additionalClassName?: string;
	openInNewTab?: boolean;
};

export const Button = ({
	children,
	onClick,
	isLink,
	href,
	openInNewTab = false,
	variant = 'primary',
	additionalClassName,
}: ButtonProps) => {
	const baseStyles = 'font-bold transition-all duration-300 inline-flex items-center justify-center';

	const getButtonVariant = () => {
		switch (variant) {
		case 'secondary':
			return clsx(
				baseStyles,
				'px-8 py-4 bg-white text-gray-700 rounded-full shadow-soft',
				'border border-gray-200 hover:border-primary-300',
				'hover:shadow-soft-lg hover:scale-105 hover:text-primary-600'
			);
		case 'tertiary':
			return clsx(
				baseStyles,
				'px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600',
				'text-white rounded-full shadow-soft',
				'hover:shadow-soft-lg hover:scale-105'
			);
		case 'outline':
			return clsx(
				baseStyles,
				'px-8 py-4 bg-transparent text-primary-600 rounded-full',
				'border-2 border-primary-500',
				'hover:bg-primary-50 hover:scale-105'
			);
		default:
			return clsx(
				baseStyles,
				'px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600',
				'text-white rounded-full shadow-soft',
				'hover:shadow-soft-lg hover:scale-105'
			);
		}
	};

	const buttonClass = clsx(getButtonVariant(), additionalClassName);

	return isLink && href ? (
		<Link
			to={href}
			className={buttonClass}
			target={openInNewTab ? '_blank' : '_self'}
		>
			{children}
		</Link>
	) : (
		<button
			onClick={onClick}
			className={buttonClass}
		>
			{children}
		</button>
	);
};
